import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Table, Button, Text, Badge, Group, Stack, Title } from '@mantine/core';
import { IconTrendingDown, IconTrendingUp } from '@tabler/icons';
import { SYMBOLS } from '../../../constants/symbols';
import { useWaveConfig, getExitFlowType } from '../../../waves/WaveConfigContext';
import ObstructionCloseModal from '../../../waves/obstruction/ObstructionCloseModal';
import { WALLET_ASSETS_USER_QUERY } from '../../../queries/OrdersAndWallets';
import './LongPositionsComponent.css';

// Helper function to format numbers
const formatNumber = (value, options = {}) => {
  const {
    prefix = '',
    suffix = '',
    decimalScale = 2,
    thousandSeparator = true,
    fixedDecimals = false  // New option to force exact decimal places
  } = options;

  if (value === null || value === undefined || value === '') return `${prefix}0${suffix}`;

  if (decimalScale === null || decimalScale === undefined) {
    return `${prefix}${value.toString()}${suffix}`;
  }

  const num = Number(value);
  if (!Number.isFinite(num)) {
    return `${prefix}${value.toString()}${suffix}`;
  }

  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: fixedDecimals ? decimalScale : 0,
    maximumFractionDigits: decimalScale,
    useGrouping: thousandSeparator
  });

  return `${prefix}${formatted}${suffix}`;
};

// Derives a stable 32-bit integer id from (walletid, symbol) for research
// logging only. CLOSE_ATTEMPTS.position_id is a bare INTEGER with no FK, so
// this does not need to reference a real DB row.
function longPositionId(walletid, symbol) {
  const s = `${walletid}:${symbol}`;
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) | 0; // 32-bit signed, wraps naturally
  }
  return Math.abs(hash);
}

export default function LongPositionsComponent({ walletid, symbol, onSellPosition }) {
  // Wave E — Obstruction: when the enrolled participant is in Phase 2/3 of an
  // obstruction wave, the "Sell" tap is gated behind the close-attempt flow
  // instead of selling immediately.
  const { config: waveConfig } = useWaveConfig();
  const [obstructionPosition, setObstructionPosition] = useState(null);
  const parsedWalletId = Number(walletid);
  const hasValidWalletId = Number.isInteger(parsedWalletId) && parsedWalletId > 0;

  const { data, loading, error } = useQuery(WALLET_ASSETS_USER_QUERY, {
    variables: { walletid: parsedWalletId },
    skip: !hasValidWalletId,
    pollInterval: 60000, // Refresh every 1 minute
    onError: (error) => {
      console.error('Wallet assets query error:', error);
    }
  });

  const positions = useMemo(() => {
    const all = data?.getWalletAssetsWalletID || [];
    return all.filter((row) => {
      const amount = Number(row.amount) || 0;
      if (amount <= 0) return false;
      if (!symbol) return true;
      return row.symbol.toUpperCase() === symbol.toUpperCase();
    });
  }, [data?.getWalletAssetsWalletID, symbol]);

  const enrichedPositions = useMemo(() => {
    return positions.map((position) => {
      const amount = Number(position.amount) || 0;
      const avgPrice = Number(position.avgPrice) || 0;
      const currentPrice = Number(position.currPrice) || 0;
      const grossPnl = Number(position.pl) || 0;
      const costBasis = avgPrice * amount;
      const pnlPercent = costBasis ? (grossPnl / costBasis) * 100 : 0;

      return {
        id: longPositionId(parsedWalletId, position.symbol),
        symbol: position.symbol,
        quantity: amount,
        entryPrice: avgPrice,
        metrics: {
          quantity: amount,
          entryPrice: avgPrice,
          currentPrice,
          marketValue: Number(position.mv) || 0,
          grossPnl,
          pnlPercent,
        },
      };
    });
  }, [positions, parsedWalletId]);

  const sellNow = (position) => {
    if (onSellPosition) {
      onSellPosition(position);
    }
  };

  const handleSellPosition = (position) => {
    // Gate the close behind the obstruction flow only when it actually applies.
    if (getExitFlowType(waveConfig) !== "normal") {
      setObstructionPosition(position);
      return;
    }
    sellNow(position);
  };

  if (loading) return <Text>Loading holdings...</Text>;
  if (error) return <Text color="red">Error loading holdings: {error.message}</Text>;

  if (enrichedPositions.length === 0) {
    return (
      <Stack spacing="md" className="long-positions-empty">
        <Title order={4}>Holdings</Title>
        <Text color="dimmed" size="sm">No holdings found</Text>
      </Stack>
    );
  }

  const rows = enrichedPositions.map((position) => {
    const { metrics } = position;
    const symbolConfig = SYMBOLS.find(s => s.code === position.symbol);

    return (
      <tr key={position.id}>
        <td>
          <Group spacing="xs">
            {symbolConfig?.icon}
            <Text weight={500}>{position.symbol}</Text>
          </Group>
        </td>
        <td>
          <Text>
            {formatNumber(metrics.quantity, {
              decimalScale: 9,
              thousandSeparator: true,
              fixedDecimals: true
            })}
          </Text>
        </td>
        <td>
          <Text>
            {formatNumber(metrics.entryPrice, {
              prefix: '$',
              decimalScale: 2,
              thousandSeparator: true
            })}
          </Text>
        </td>
        <td>
          <Text color={metrics.currentPrice > 0 ? undefined : 'dimmed'}>
            {metrics.currentPrice ? formatNumber(metrics.currentPrice, {
              prefix: '$',
              decimalScale: 2,
              thousandSeparator: true
            }) : 'Loading...'}
          </Text>
        </td>
        <td>
          <Text>
            {formatNumber(metrics.marketValue, {
              prefix: '$',
              decimalScale: 2,
              thousandSeparator: true
            })}
          </Text>
        </td>
        <td>
          <Group spacing="xs">
            <Text
              color={metrics.grossPnl >= 0 ? 'green' : 'red'}
              weight={500}
            >
              {formatNumber(Math.abs(metrics.grossPnl), {
                prefix: metrics.grossPnl >= 0 ? '+$' : '-$',
                decimalScale: 2,
                thousandSeparator: true
              })}
            </Text>
            <Badge
              color={metrics.grossPnl >= 0 ? 'green' : 'red'}
              variant="light"
              leftSection={metrics.grossPnl >= 0 ? <IconTrendingUp size={12} /> : <IconTrendingDown size={12} />}
            >
              {metrics.pnlPercent >= 0 ? '+' : ''}{metrics.pnlPercent.toFixed(2)}%
            </Badge>
          </Group>
        </td>
        <td>
          <Button
            size="xs"
            color="red"
            variant="outline"
            onClick={() => handleSellPosition(position)}
          >
            Sell
          </Button>
        </td>
      </tr>
    );
  });

  return (
    <Stack spacing="md" className="long-positions-container">
      <Title order={4}>Holdings</Title>

      <Table striped highlightOnHover>
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Quantity</th>
            <th>Avg Price</th>
            <th>Current Price</th>
            <th>Market Value</th>
            <th>P&amp;L</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </Table>

      {obstructionPosition && (
        <ObstructionCloseModal
          opened={!!obstructionPosition}
          position={obstructionPosition}
          waveConfig={waveConfig}
          onComplete={() => {
            const pos = obstructionPosition;
            setObstructionPosition(null);
            sellNow(pos);
          }}
          onAbandon={() => setObstructionPosition(null)}
          onFallbackClose={() => {
            const pos = obstructionPosition;
            setObstructionPosition(null);
            sellNow(pos);
          }}
        />
      )}
    </Stack>
  );
}
